import { formatTime } from "@clipboard-health/date-time";
import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { ChatMessageTimestamp } from "../ChatMessageTimestamp";
import type { AdminChatMessage } from "../types";
import { parseCustomMessage } from "../utils/parseCustomMessage";
import { ChatMessageBody } from "./ChatMessageBody";

interface WorkerLockNotificationMessageProps {
  message: AdminChatMessage;
  sx?: SxProps<Theme>;
}

/**
 * Renders a worker lock notification as an outgoing message bubble with preserved newlines.
 * Visually identical to a regular text message sent from the workplace.
 */
export function WorkerLockNotificationMessage(props: WorkerLockNotificationMessageProps) {
  const { message, sx } = props;
  const displayMessage = parseCustomMessage(message.message);

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        },
        sx
      )}
    >
      <ChatMessageTimestamp
        senderName="Clipboard Health"
        timestamp={formatTime(new Date(message.createdAt))}
      />
      <Box
        sx={(theme) => ({
          display: "inline-flex",
          flexDirection: "column",
          gap: 1,
          paddingX: 4,
          paddingY: 3,
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: theme.palette.background.chat?.outgoing ?? theme.palette.primary.main,
          maxWidth: "80%",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        })}
      >
        <ChatMessageBody text={displayMessage} isIncoming={false} />
      </Box>
    </Box>
  );
}
